import { getPositionAndMaxHeightVertically } from '../../dropdownHelpers';
import { ActionCreatorWithoutNamespace } from '@ardoq/rxbeach';
import { ModalBox } from '../ModalBox';
import { AddListener } from '../modalDropdownTypes';
import { GetBox } from '../WithDropdown';
import { SelectTextInput, SelectTextInputInDropdown } from './SelectTextInput';
import { useOnOptionsChangedSubscription } from './useOnOptionsChangedSubscription';
import { useSubscribeToKeyboardListener } from './useSubscribeToKeyboardListener';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import type { I18nLocale } from '@ardoq/i18n';

type DropdownContentProps<T = any> = {
  clickNamespace?: string;
  getHostBox: GetBox;
  addKeyboardListener: AddListener;
  removeKeyboardListener: AddListener;
  placeholderText: string;
  children: React.ReactNode;
  forceAlignMenuToRight: boolean;
  modalBoxVerticalOffset: number;
  onOptionsChanged: (value: T) => void;
  actionNamespace?: string;
  optionsChangedAction: ActionCreatorWithoutNamespace<T>;
  onFilterChanged?: (filterTerm: string) => void;
  isFindMaxHeight?: boolean;
  defaultValue?: string;
  onClear?: () => void;
  closeModal: () => void;
  minDropdownSize?: DropdownSize;
  hasCustomSelectButton: boolean;
  hasSearchInputInDropdown: boolean;
  additionalSearchControls?: React.ReactNode;
  i18nLocale?: I18nLocale;
};

export const CustomSelectBaseDropdownContent = <T = any,>({
  clickNamespace,
  getHostBox,
  addKeyboardListener,
  removeKeyboardListener,
  placeholderText,
  children,
  forceAlignMenuToRight,
  modalBoxVerticalOffset,
  onOptionsChanged,
  actionNamespace,
  optionsChangedAction,
  onFilterChanged,
  isFindMaxHeight = true,
  defaultValue,
  onClear,
  closeModal,
  minDropdownSize,
  hasCustomSelectButton,
  hasSearchInputInDropdown,
  additionalSearchControls,
  i18nLocale,
}: DropdownContentProps<T>) => {
  useOnOptionsChangedSubscription<T>(
    onOptionsChanged,
    optionsChangedAction,
    actionNamespace
  );
  useSubscribeToKeyboardListener({
    addKeyboardListener,
    removeKeyboardListener,
    actionNamespace,
  });
  return (
    <>
      {!hasSearchInputInDropdown && (
        <SelectTextInput
          clickNamespace={clickNamespace}
          getHostBox={getHostBox}
          placeholderText={placeholderText}
          onFilterChanged={onFilterChanged}
          defaultValue={defaultValue}
          onClear={onClear}
          closeModal={closeModal}
          hasCustomSelectButton={hasCustomSelectButton}
          i18nLocale={i18nLocale}
        />
      )}
      <ModalBox
        clickNamespace={clickNamespace}
        getHostBox={getHostBox}
        getPositionAndMaxHeight={(refBox, modalBox) => {
          return getPositionAndMaxHeightVertically({
            refBox,
            modalBox: new DOMRect(
              modalBox.left,
              modalBox.top,
              modalBox.width,
              // Make sure the modal box takes the maximum
              // height possible when opening.
              modalBox.height < 50 || isFindMaxHeight
                ? window.innerHeight
                : modalBox.height
            ),
            isScrollable: true,
            forceAlignToRight: forceAlignMenuToRight,
            verticalOffset: modalBoxVerticalOffset,
            includeBottom: true,
          });
        }}
        isScrollLockDisabled={true}
        noScrollOnWrapper={true}
        useDisplayFlexForMainContent={true}
        setContentWidthToHostWidth={true}
        minDropdownSize={minDropdownSize}
      >
        {hasSearchInputInDropdown && (
          <SelectTextInputInDropdown
            clickNamespace={clickNamespace}
            getHostBox={getHostBox}
            placeholderText={placeholderText}
            onFilterChanged={onFilterChanged}
            defaultValue={defaultValue}
            onClear={onClear}
            closeModal={closeModal}
            hasCustomSelectButton={hasCustomSelectButton}
            additionalSearchControls={additionalSearchControls}
            i18nLocale={i18nLocale}
          />
        )}
        {children}
      </ModalBox>
    </>
  );
};
